import {
  Box,
  Button,
  Checkbox,
  FormControl,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
} from "@chakra-ui/react";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { t } from "i18next";
import { ReactElement, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import {
  ContractDto,
  ContractRoleType,
  MinimalOrgDto,
  PersonDto,
  OrganizationType,
  useCreateContractParticipantMutation,
  useGetOrganizationQuery,
} from "../../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../../common/auth/useLoggedInWithOrgContextState";
import { useApiError } from "../../../../common/errors/useApiError";
import { RadioFormSelector } from "../../../../common/input/Radio/RadioFormSelector";
import { OrganizationFormSelector } from "../../../../common/input/Selector/OrganizationFormSelector";
import { PersonSelectorForm } from "../../../../common/persons/PersonSelectorForm";

interface Props {
  data: ContractDto;
  onClose: () => void;
}

interface FormValues {
  organization?: MinimalOrgDto;
  person: PersonDto | null;
  roleType: ContractRoleType;
  shouldNotifyByEmail: boolean;
}

export const AddExternalParticipantModal = ({ data, onClose }: Props) => {
  const toast = useToast();
  const displayer = useApiError();
  const authState = useLoggedInWithOrgContextState();

  const [createParticipant] = useCreateContractParticipantMutation();

  const methods = useForm<FormValues>({
    defaultValues: { roleType: ContractRoleType.Editor, shouldNotifyByEmail: true, person: null },
  });
  const [isLoading, setIsLoading] = useState(false);

  const minimalOrg = methods.watch("organization");

  const { data: organization, isFetching: isLoadingOrganization } = useGetOrganizationQuery(
    minimalOrg?.id ? { orgId: minimalOrg.id } : skipToken
  );

  const create = async (values: FormValues) => {
    if (!values.organization) return;
    setIsLoading(true);
    const result = await createParticipant({
      contractId: data.id,
      createContractParticipantRequest: {
        organizationId: values.organization.id,
        personId: values.person?.id,
        roleType: values.roleType,
        shouldNotifyByEmail: values.shouldNotifyByEmail,
      },
    });
    if ("data" in result) {
      toast({ title: t("Participant added!"), status: "success" });
      onClose();
    } else displayer.trigger(result.error);
  };

  const options: {
    label: string;
    description: ReactElement;
    value: ContractRoleType;
  }[] = [
    {
      label: t("Editor"),
      description: (
        <Text fontSize="sm" color="smMuted">
          {t("Can see and edit everything")}.
        </Text>
      ),
      value: ContractRoleType.Editor,
    },
    {
      label: t("Viewer"),
      description: (
        <Text fontSize="sm" color="smMuted">
          {t("Can see everything, but not edit anything")}.
        </Text>
      ),
      value: ContractRoleType.Viewer,
    },
    {
      label: t("Counterparty"),
      description: (
        <>
          <Text fontSize="sm" color="smMuted">
            {t("Can see all details, data-fields and documents")}.
          </Text>{" "}
          <Text fontSize="sm" color="smMuted">
            {t("Can see and edit all tasks that are delegated to them")}.
          </Text>
        </>
      ),
      value: ContractRoleType.Counterparty,
    },
  ];

  return (
    <Modal isOpen={true} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t("Share contract externally")}</ModalHeader>
        <ModalCloseButton />
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(create)}>
            <ModalBody>
              <OrganizationFormSelector
                type={OrganizationType.Organization}
                label={t("Organization")}
                helperText={t("Please select an organization to share this contract with")}
                control={methods.control}
                name="organization"
                orgsToExclude={[authState.selectedOrg.id]}
                required={{ value: true, message: t("Please select an organization") }}
                errorMessage={methods.formState.errors.organization?.message}
              />
              <Box pt="8">
                <PersonSelectorForm<FormValues>
                  organizationIds={organization?.id ? [organization.id] : []}
                  isDisabled={!organization || minimalOrg?.id !== organization?.id}
                  isLoading={isLoadingOrganization}
                  size="sm"
                  label={t("Share with specific person in organization (optional)")}
                  control={methods.control}
                  name={"person"}
                  helperText={`${t("Select a person to share the contract with")}.`}
                  placeholderText={`${t("Select a person from the selected organization")}...`}
                  errorMessage={undefined}
                />
              </Box>
              <Box pt="8">
                <RadioFormSelector options={options} label={t("Role")} control={methods.control} name={"roleType"} />
              </Box>
              <FormControl pt="5">
                <Controller<FormValues>
                  name={"shouldNotifyByEmail"}
                  control={methods.control}
                  render={({ field: { onChange, value } }) => (
                    <Checkbox isChecked={!!value} name="shouldNotifyByEmail" onChange={onChange}>
                      {t("Send email notification to participant(s)")}
                    </Checkbox>
                  )}
                />
              </FormControl>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isLoading}>
                {t("Cancel")}
              </Button>
              <Button type="submit" variant="solid" colorScheme={"blue"} isLoading={isLoading}>
                {t("Add")}
              </Button>
            </ModalFooter>
          </form>
        </FormProvider>
      </ModalContent>
    </Modal>
  );
};

export const displayPersonNameWithEmail = (person: PersonDto): string => {
  const name = `${person.firstName ?? ""} ${person.lastName ?? ""}`;
  return person.email ? `${name} (${person.email})` : name.trim();
};

export const displayPersonName = (person: PersonDto): string => {
  return `${person.firstName ?? ""} ${person.lastName ?? ""}`;
};
